/** @jsx jsx */

import type { WithDataAttributeProp } from '@balance-web/core';
import { getTestIdFromDataAttributes, jsx } from '@balance-web/core';
import { HelpCircleIcon } from '@balance-web/icon/icons/HelpCircleIcon';
import { useTheme } from '@balance-web/theme';
import { makeIdFactory } from '@balance-web/utils';
import { useMemo } from 'react';

import type { TooltipProps } from './Tooltip';
import { Tooltip } from './Tooltip';

// The 'field-help' prefix predates this component's move here from
// @balance-web/field; it is kept so existing testids stay stable.
const HelpIndicatorIdFactory = makeIdFactory('field-help');

const helpIndicatorCss = {
  height: 20,
  width: 20,
} as const;

export type HelpIndicatorProps = WithDataAttributeProp<{
  /** The ID of the element. */
  id?: string;
  /** The content of the tooltip. */
  content: TooltipProps['content'];
  placement?: 'top' | 'right' | 'bottom' | 'left';
}>;

export const HelpIndicator = (props: HelpIndicatorProps) => {
  const { spacing } = useTheme();
  const testids = HelpIndicatorIdFactory();
  const testid = useMemo(() => {
    const existing = getTestIdFromDataAttributes(props.data);
    if (existing) {
      return existing;
    }

    return testids.base(props.id);
  }, []);

  return (
    <Tooltip
      content={props.content}
      data={{ testid }}
      data-balance-component="field-help-indicator"
      id={props.id}
      placement={props.placement}
    >
      {/* A real button, not a span: screen readers only announce the
          tooltip (the accessible description) reliably for interactive
          controls — VoiceOver reads a labelled span as a bare "group" and
          skips the description. */}
      <button
        type="button"
        aria-label="Help"
        css={{
          alignItems: 'center',
          background: 0,
          border: 0,
          cursor: 'help',
          display: 'flex',
          justifyContent: 'center',
          marginLeft: spacing.xsmall,
          padding: 0,
          position: 'relative',

          // increase hit target
          '::before': {
            bottom: -spacing.xsmall,
            content: '" "',
            left: -spacing.xsmall,
            position: 'absolute',
            right: -spacing.xsmall,
            top: -spacing.xsmall,
          },
        }}
      >
        <HelpCircleIcon
          color="textTertiary"
          css={helpIndicatorCss}
          size="small"
        />
      </button>
    </Tooltip>
  );
};
